import React, { useState } from "react";
import { motion } from "motion/react";
import { toast } from "sonner";
import { Send, MapPin, Mail, Phone, ExternalLink, Copy, Check } from "lucide-react";
import { projectId, publicAnonKey } from "../../../utils/supabase/info";

export function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyEmail = () => {
    navigator.clipboard.writeText("ergysonuzi12@gmail.com");
    setCopied(true);
    toast.success("Email copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-5f13ee94/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${publicAnonKey}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error("Failed to send message to server");
      }

      toast.success("Message sent successfully!", {
        description: "Thanks for reaching out, Ergys will get back to you soon.",
      });
      
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Error sending message", {
        description: "Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: <Mail size={20} />, label: "Email", value: "ergysonuzi12@gmail.com", href: "mailto:ergysonuzi12@gmail.com", canCopy: true },
    { icon: <Phone size={20} />, label: "Phone", value: "+383 43 829 718", href: "tel:+38343829718" },
    { icon: <MapPin size={20} />, label: "Location", value: "Prizren, Kosovo", href: "#" },
  ];

  return (
    <div className="grid md:grid-cols-2 gap-16 items-center">
      <motion.div
        initial={{ opacity: 0, x: -30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-full bg-violet-600/20 flex items-center justify-center text-violet-400">
            <Mail size={18} />
          </div>
          <span className="text-xs font-black uppercase tracking-[0.4em] text-violet-400">Contact Me</span>
        </div>
        
        <h3 className="text-4xl md:text-6xl font-black mb-8 uppercase tracking-tighter leading-none italic">
          Let's Build <br />
          <span className="bg-gradient-to-r from-violet-500 to-fuchsia-500 bg-clip-text text-transparent">Something Big</span>
        </h3>
        
        <p className="text-neutral-500 mb-12 max-w-lg leading-relaxed text-lg font-medium">
          I'm currently available for freelance work and technical consultations. 
          If you have a project that needs a <span className="text-white">full-stack touch</span>, reach out.
        </p>

        <div className="space-y-6">
          {contactInfo.map((info) => (
            <div key={info.label} className="flex items-center gap-6 group p-4 -ml-4 rounded-3xl hover:bg-neutral-900/50 transition-all duration-500 relative">
              <div className="bg-neutral-900 border border-neutral-800/50 p-5 rounded-2xl group-hover:border-violet-500/50 group-hover:shadow-[0_0_20px_rgba(139,92,246,0.15)] transition-all duration-500 text-neutral-400 group-hover:text-violet-400">
                {info.icon}
              </div>
              <div className="flex-1">
                <p className="text-[10px] font-black uppercase tracking-[0.3em] text-neutral-600 mb-1">{info.label}</p>
                <a href={info.href} className="text-xl font-black text-white tracking-tight group-hover:text-violet-300 transition-colors block">{info.value}</a>
              </div>
              {info.canCopy && (
                <button 
                  onClick={copyEmail}
                  className="p-3 bg-neutral-950 border border-neutral-800 rounded-xl text-neutral-500 hover:text-white hover:border-violet-500 transition-all"
                  title="Copy Email"
                >
                  {copied ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="mt-16 pt-10 border-t border-neutral-900/50 flex gap-10">
          {[
            { name: "GitHub", href: "https://github.com/ErgysOnuzi", label: "Visit Ergys Onuzi on GitHub" },
            { name: "LinkedIn", href: "https://al.linkedin.com/in/ergys-onuzi-3131aa193", label: "Visit Ergys Onuzi on LinkedIn" }
          ].map((social) => (
            <a 
              key={social.name} 
              href={social.href} 
              target="_blank" 
              rel="noreferrer" 
              className="text-xs font-black uppercase tracking-[0.3em] text-neutral-600 hover:text-white transition-colors flex items-center gap-2 group"
              aria-label={social.label}
            >
              {social.name} <ExternalLink size={14} className="opacity-0 group-hover:opacity-100 transition-all translate-y-1 group-hover:translate-y-0" aria-hidden="true" />
            </a>
          ))}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
        className="bg-neutral-900/40 backdrop-blur-xl border border-neutral-800/50 p-8 md:p-16 rounded-[2.5rem] shadow-2xl relative overflow-hidden group"
      >
        <div className="absolute top-0 right-0 -mr-24 -mt-24 w-80 h-80 bg-violet-600/10 blur-3xl rounded-full group-hover:bg-violet-600/20 transition-all duration-700" />
        
        <form onSubmit={handleSubmit} className="space-y-8 relative z-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <div className="space-y-3">
              <label className="text-xs font-black uppercase tracking-[0.3em] text-neutral-500 pl-1">Full Name</label>
              <input
                required
                type="text"
                placeholder="Name"
                className="w-full bg-neutral-950/50 border border-neutral-800/50 rounded-2xl px-6 py-5 focus:outline-none focus:border-violet-500/50 transition-all font-bold text-white placeholder:text-neutral-700"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-3">
              <label className="text-xs font-black uppercase tracking-[0.3em] text-neutral-500 pl-1">Email Address</label>
              <input
                required
                type="email"
                placeholder="email@example.com"
                className="w-full bg-neutral-950/50 border border-neutral-800/50 rounded-2xl px-6 py-5 focus:outline-none focus:border-violet-500/50 transition-all font-bold text-white placeholder:text-neutral-700"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-3">
            <label className="text-xs font-black uppercase tracking-[0.3em] text-neutral-500 pl-1">Subject</label>
            <input
              required
              type="text"
              placeholder="Web Development Request"
              className="w-full bg-neutral-950/50 border border-neutral-800/50 rounded-2xl px-6 py-5 focus:outline-none focus:border-violet-500/50 transition-all font-bold text-white placeholder:text-neutral-700"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            />
          </div>

          <div className="space-y-3">
            <label className="text-xs font-black uppercase tracking-[0.3em] text-neutral-500 pl-1">Message</label>
            <textarea
              required
              rows={5}
              placeholder="Project details..."
              className="w-full bg-neutral-950/50 border border-neutral-800/50 rounded-2xl px-6 py-5 focus:outline-none focus:border-violet-500/50 transition-all font-bold text-white placeholder:text-neutral-700 resize-none"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            />
          </div>

          <button
            disabled={isSubmitting}
            type="submit"
            className="group/btn relative w-full bg-white text-black py-6 rounded-2xl font-black uppercase tracking-[0.3em] text-xs flex items-center justify-center gap-3 hover:bg-violet-600 hover:text-white transition-all duration-500 active:scale-[0.98] disabled:opacity-50 overflow-hidden"
          >
            <span className="relative z-10">{isSubmitting ? "Sending..." : "Send Message"}</span>
            {!isSubmitting && <Send size={18} className="relative z-10 group-hover/btn:translate-x-2 group-hover/btn:-translate-y-1 transition-transform duration-500" />}
            <div className="absolute inset-0 bg-gradient-to-r from-violet-600 to-fuchsia-600 opacity-0 group-hover/btn:opacity-100 transition-opacity" />
          </button>
        </form>
      </motion.div>
    </div>
  );
}